import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Trans, useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { QUOTE_CROSSFADE_TRANSITION, ROTATION_INTERVAL_MS } from './motion'

/** Locked quote-slot height (4 lines + cite). Rem, not a measured px — jsdom layout is 0. */
export const PORTRAIT_MOSAIC_QUOTE_BLOCK_HEIGHT_CLASS = 'h-[10.5rem]'

/** 4-line clamp on the verbatim. Pair with the locked height class above. */
export const PORTRAIT_MOSAIC_QUOTE_CLAMP_CLASS = 'line-clamp-4'

/**
 * One rotating quote (S2, docs/plans/portrait-mosaic-design-upgrade.md).
 * `attribution` is already-formatted plain text ("Name, age · role · city") —
 * the caller builds it via the `quoteIdentityBlurb` path (see
 * apps/frontend/src/components/study-doc/enrichStudyDocQuote.ts and
 * apps/frontend/src/components/quick-study-results/QuickStudyTwinsPanel.tsx).
 * This component only renders the string verbatim: no glyphs, no uppercase,
 * no letter-spacing (docs/plans/portrait-mosaic-strip-ui.md typography rules).
 */
export interface PortraitMosaicQuote {
  /** `personas.token`. Matches a rendered tile → that tile becomes the ringed, lit speaker. */
  personaId?: string
  text: string
  attribution?: string
  /** First-ranked / picked concept (D4). Renders as "ranked X first" — accent on the name only. */
  versionLabel?: string
  /**
   * How the cite phrases {@link versionLabel}: `'ranked'` (default, today's copy) reads
   * "ranked X first"; `'chose'` reads "chose X" for a single/multiple choice question,
   * where the respondent picked an option rather than ordering them (Step 5b visual QA
   * finding 5). Accent styling and the absence of any dot glyph are identical either way.
   */
  versionLabelKind?: 'ranked' | 'chose'
}

interface PortraitMosaicQuoteBlockProps {
  quotes: Array<PortraitMosaicQuote>
  activeIndex: number
  setActiveIndex: Dispatch<SetStateAction<number>>
  /**
   * External pause, OR-ed with this block's own hover/hidden pauses. The strip sets it
   * after a tile click so a reader who picked a speaker keeps them on screen; the strip
   * clears it again once the idle window elapses.
   */
  isPaused?: boolean
  /**
   * Advance to the next quoted person. Set → the block carries a full-size button layer
   * ("Next quote"): clicking the quote is the same gesture as clicking a tile (ring and
   * color move, rotation holds for the idle window). Omitted, or a single-quote block
   * (kpi mode's supporting quote), renders no button and no pointer affordance.
   */
  onAdvance?: () => void
  /**
   * How many LEADING entries of `quotes` the idle timer and the advance button cycle
   * through — the curated set, when the caller appends extra row-sourced quotes after it
   * (see `PortraitMosaicStrip`). Clamped to `[1, quotes.length]`; omitted or out of range
   * falls back to `quotes.length`, i.e. today's behavior (rotate everything). A tile click
   * can still jump `activeIndex` to any entry, curated or not — this only bounds what the
   * timer/button cycle back into.
   */
  rotationCount?: number
  className?: string
}

/** Clamp to a usable rotation window: never 0 (divide-by-zero), never past the array. */
function clampRotationCount(rotationCount: number | undefined, quoteCount: number): number {
  const requested = rotationCount ?? quoteCount
  return Math.max(1, Math.min(requested, quoteCount))
}

/**
 * Rotating quote beneath the strip. Advances on a timer that is disabled
 * entirely under reduced motion, and paused (never advanced, never reset)
 * while the tab is hidden or the block is hovered/focused. Quote changes
 * crossfade via `AnimatePresence`, slot-keyed on the active index.
 */
export function PortraitMosaicQuoteBlock(props: PortraitMosaicQuoteBlockProps) {
  const {
    quotes,
    activeIndex,
    setActiveIndex,
    isPaused: isPausedByCaller,
    onAdvance,
    rotationCount,
    className,
  } = props

  const effectiveRotationCount = clampRotationCount(rotationCount, quotes.length)

  const { t } = useTranslation('app')
  const shouldReduceMotion = useReducedMotion()
  const [isHidden, setIsHidden] = useState(() =>
    typeof document === 'undefined' ? false : document.hidden,
  )
  const [isHovered, setIsHovered] = useState(false)
  const isPaused = isHidden || isHovered || Boolean(isPausedByCaller)

  useEffect(() => {
    function handleVisibilityChange() {
      setIsHidden(document.hidden)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (shouldReduceMotion || isPaused || effectiveRotationCount <= 1) {
      return
    }
    const timer = window.setInterval(() => {
      // Modulo against the ROTATION window, not the full `quotes` array: `current` can sit
      // past `effectiveRotationCount` right after a tile click selected a row-sourced quote
      // beyond the curated set, and this must still land inside [0, effectiveRotationCount)
      // on the very next tick — never NaN/undefined, never re-selecting the clicked persona.
      setActiveIndex((current) => (current + 1) % effectiveRotationCount)
    }, ROTATION_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [shouldReduceMotion, isPaused, effectiveRotationCount, setActiveIndex])

  const safeIndex = Math.min(activeIndex, quotes.length - 1)
  const active = quotes[safeIndex]

  if (!active) {
    return null
  }

  const versionLabel = (active.versionLabel || '').trim()
  const hasCite = Boolean(active.attribution || versionLabel)

  function handlePauseStart() {
    setIsHovered(true)
  }

  function handlePauseEnd() {
    setIsHovered(false)
  }

  // Gated on the ROTATION window, not `quotes.length`: with 0 or 1 curated entries and
  // only row-sourced quotes past them, advancing would always land back on the same
  // single entry — no point showing the "next" affordance for that.
  const canAdvance = Boolean(onAdvance) && effectiveRotationCount > 1

  return (
    <div
      className={cn(
        'relative mx-auto mt-4 max-w-[42rem] overflow-hidden px-6',
        PORTRAIT_MOSAIC_QUOTE_BLOCK_HEIGHT_CLASS,
        canAdvance && 'cursor-pointer',
        className,
      )}
      data-testid="portrait-mosaic-quote-block"
      aria-live="polite"
      tabIndex={0}
      onMouseEnter={handlePauseStart}
      onMouseLeave={handlePauseEnd}
      onFocus={handlePauseStart}
      onBlur={handlePauseEnd}
    >
      {/*
        The whole quote is the affordance ("should be clickable the quotes", live review
        2026-09-03), rendered as a real button layered over the slot rather than as a
        wrapper: the quote is built from <p> elements, which may not nest inside a button.
        It sits above the crossfading text, so it stays hit-testable while the quote swaps,
        and it adds no height of its own — the locked slot is unchanged.
      */}
      {canAdvance ? (
        <button
          type="button"
          className="absolute inset-0 z-10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--brand-accent,var(--primary)))]"
          aria-label={t('studyDocChrome.nextQuote')}
          data-testid="portrait-mosaic-quote-next"
          onClick={onAdvance}
        />
      ) : null}
      {/*
        Absolute crossfade inside a rem-locked slot: rotation must never grow the
        box (addendum locked height). Both quotes overlay; the container height is
        the class above, not the verbatim length.
      */}
      <AnimatePresence initial={false}>
        <motion.div
          key={safeIndex}
          className="absolute inset-x-0 top-0"
          initial={shouldReduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={shouldReduceMotion ? undefined : { opacity: 0 }}
          transition={QUOTE_CROSSFADE_TRANSITION}
          data-testid="portrait-mosaic-quote"
          data-persona-id={active.personaId ?? ''}
        >
          {/*
            Typographic quote marks read as a verbatim ("should read like a quote", live
            QA 2026-09-03) — added purely via CSS `::before`/`::after` generated content
            so `active.text` itself never carries the glyphs: the underlying quote DATA
            stays exactly what the respondent said, this is rendering only. Curly quotes,
            never an em/en dash (global copy rule — irrelevant here, but noted since this
            is the same user-facing-copy surface that rule polices).
          */}
          {/*
            `title` exposes the FULL, unclamped verbatim on hover — the 4-line clamp below
            can truncate a long row-sourced answer mid-sentence, and there is no other
            surface to read the rest from (live QA 2026-09-03). Always-on rather than
            conditioned on actual overflow: cheap, harmless on a short quote, and avoids a
            layout-measuring effect just to gate a browser-native tooltip.
          */}
          <p
            className={cn(
              "font-sans text-[1.15rem] font-normal not-italic leading-[1.45] tracking-normal sm:text-[1.35rem] before:content-['“'] after:content-['”']",
              PORTRAIT_MOSAIC_QUOTE_CLAMP_CLASS,
            )}
            title={active.text}
          >
            {active.text}
          </p>
          {hasCite ? (
            <p
              className="mt-2 font-sans text-sm font-normal not-italic tracking-normal text-muted-foreground"
              data-testid="portrait-mosaic-quote-attribution"
            >
              {active.attribution ? active.attribution : null}
              {versionLabel ? (
                <>
                  {active.attribution ? ' · ' : ''}
                  <Trans
                    i18nKey={
                      active.versionLabelKind === 'chose'
                        ? 'studyDocChrome.chosePicked'
                        : 'studyDocChrome.rankedFirst'
                    }
                    t={t}
                    values={{ label: versionLabel }}
                    components={{
                      accent: (
                        // Light mode keeps the picked-concept name as strong band ink
                        // (medium weight against the muted cite around it) — the
                        // reference's neon accent is a dark-mode-only treatment and was
                        // explicitly rejected for light mode (Fernando, 2026-09-03). Dark
                        // mode keeps the brand accent, which reads well on the deep slab.
                        <span
                          className="font-medium not-italic text-foreground dark:text-[hsl(var(--brand-accent,var(--primary)))]"
                          data-testid="portrait-mosaic-quote-ranked-label"
                          data-version-label-kind={active.versionLabelKind ?? 'ranked'}
                        />
                      ),
                    }}
                  />
                </>
              ) : null}
            </p>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
